import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plane, ShieldCheck, Package, ArrowRight, Instagram, Sparkles, Star, Quote } from "lucide-react";
import { api, resolveImg } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import ProductCard from "@/components/ProductCard";
import { FlightPathDivider, AirplaneLogo, PassportStamp } from "@/components/motifs";

const CATEGORIES = [
  { name: "Rings", tagline: "Encircling Grace" },
  { name: "Necklaces", tagline: "Golden Horizons" },
  { name: "Earrings", tagline: "Whispers of Starlight" },
  { name: "Bracelets", tagline: "Wrists of Royalty" },
  { name: "Anklets", tagline: "Delicate Footsteps" },
];

const ImagePlaceholder = ({ name, testid }) => (
  <div data-testid={testid} aria-label={name} className="flex h-full w-full items-start justify-center bg-gradient-to-b from-[#F6F1E8] to-[#EADFCC] pt-[22%]">
    <div className="opacity-60"><AirplaneLogo size={34} /></div>
  </div>
);

const SEGMENTS = [
  { key: "Anti-Tarnish", slug: "anti-tarnish", tag: "Gleam that lasts", desc: "Gold & rose-gold tone pieces with a long-lasting anti-tarnish, skin-friendly finish.", field: "segment_at_image", altField: "segment_at_alt" },
  { key: "Oxidised", slug: "oxidised", tag: "Antique soul", desc: "Boho oxidised silver-tone jhumkas, chokers & cuffs with an antique, artisanal look.", field: "segment_ox_image", altField: "segment_ox_alt" },
];

export default function Home() {
  const { settings, offers } = useStore();
  const [featured, setFeatured] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [collections, setCollections] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/products").then(({ data }) => setFeatured(data.filter((p) => p.featured).slice(0, 8)));
    api.get("/testimonials").then(({ data }) => setTestimonials(data)).catch(() => {});
    api.get("/collections").then(({ data }) => setCollections(data)).catch(() => {});
  }, []);

  const findCol = (name) => collections.find((c) => (c.name || "").trim().toLowerCase() === name.toLowerCase()) || {};

  const banners = (settings?.banners || []).filter((b) => b && b.enabled !== false && (b.image || b.heading));

  useEffect(() => {
    if (window.location.hash) {
      const el = document.querySelector(window.location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 300);
    }
  }, []);

  // Honor admin-configured homepage section order & visibility
  useEffect(() => {
    const secs = settings?.homepage_sections;
    if (!secs) return;
    secs.forEach((s, i) => {
      const el = document.querySelector(`[data-section="${s.key}"]`);
      if (el) { el.style.order = String(i); el.style.display = s.enabled === false ? "none" : ""; }
    });
  }, [settings]);

  return (
    <div className="flex flex-col">
      {/* HERO */}
      <section data-section="hero" className="relative overflow-hidden bg-[#F3EDE2]">
        <div className="mx-auto grid max-w-7xl items-center gap-8 px-4 py-14 sm:px-6 lg:grid-cols-2 lg:gap-10 lg:px-8 lg:py-24">
          <div className="fade-up">
            <div className="mb-5 flex items-center gap-2">
              <div className="h-px w-10 bg-amber-600/50" />
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-amber-700">
                Passport to Eternal Radiance
              </span>
            </div>
            <h1 className="font-serif text-4xl font-light leading-[1.05] tracking-tight text-stone-900 sm:text-5xl lg:text-6xl">
              {settings?.hero_headline || "Destination: Unrivaled Luxury"}
            </h1>
            <p className="mt-6 max-w-md font-sans text-base leading-relaxed text-stone-600 sm:text-lg">
              {settings?.hero_subheading ||
                "Fine anti-tarnish & oxidised jewellery, delivered in our signature golden passport flight box."}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button
                data-testid="hero-shop-button"
                onClick={() => navigate("/shop")}
                className="group flex items-center gap-2 rounded-full gold-gradient px-7 py-3.5 text-sm font-semibold uppercase tracking-wider text-stone-900 transition-transform hover:scale-105"
              >
                Explore Collections
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </button>
              <button
                data-testid="hero-offers-button"
                onClick={() => document.querySelector("#offers")?.scrollIntoView({ behavior: "smooth" })}
                className="rounded-full border border-stone-800 px-7 py-3.5 text-sm font-semibold uppercase tracking-wider text-stone-800 transition-colors hover:bg-stone-900 hover:text-cream"
              >
                Boarding Pass Offers
              </button>
            </div>
            <div className="mt-10 flex items-center gap-6">
              {[
                [Sparkles, "Anti-Tarnish Finish"],
                [Plane, "Insured Air Delivery"],
                [Package, "Passport Gift Box"],
              ].map(([Icon, label]) => (
                <div key={label} className="flex items-center gap-2">
                  <Icon size={18} className="text-amber-700" />
                  <span className="text-xs text-stone-600">{label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="relative overflow-hidden rounded-[2rem] border border-[#E7DFD3] shadow-2xl">
              <img
                src={resolveImg(settings?.hero_image) || "/products/hero.jpg"}
                alt="PRIYARAA luxury jewellery"
                className="h-[380px] w-full object-cover sm:h-[520px]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
            </div>
            {/* Floating passport stamp */}
            <div className="absolute -left-3 top-8 hidden sm:block animate-float">
              <PassportStamp label="PRIYARAA · EST. 2026" className="scale-125 !bg-white shadow-lg" />
            </div>
            <div className="absolute -bottom-5 right-4 flex items-center gap-3 rounded-2xl border border-[#E7DFD3] bg-white/95 px-5 py-3 shadow-xl backdrop-blur">
              <AirplaneLogo size={38} />
              <div>
                <p className="font-serif text-sm text-stone-900">First-Class Delivery</p>
                <p className="font-mono text-[9px] uppercase tracking-widest text-stone-500">Gate A1 · Boarding now</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SEGMENTS */}
      <section data-section="segments" className="mx-auto w-full max-w-7xl px-4 pt-16 sm:px-6 lg:px-8 lg:pt-24">
        <div className="text-center">
          <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-amber-700">Two Signature Finishes</span>
          <h2 className="mt-2 font-serif text-3xl tracking-tight text-stone-900 sm:text-4xl">Choose Your Journey</h2>
        </div>
        <div className="mt-8 grid gap-5 sm:grid-cols-2">
          {SEGMENTS.map((s) => { const img = settings?.[s.field]; const alt = settings?.[s.altField]; return (
            <button
              key={s.key}
              data-testid={`segment-tile-${s.slug}`}
              onClick={() => navigate(`/shop?segment=${encodeURIComponent(s.key)}`)}
              className="group relative overflow-hidden rounded-2xl border border-[#E7DFD3]"
            >
              <div className="aspect-[4/3] overflow-hidden bg-[#F3EDE2] sm:aspect-[16/10]">
                {img
                  ? <img data-testid={`segment-img-${s.slug}`} src={resolveImg(img)} alt={alt || `${s.key} jewellery — PRIYARAA`} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  : <ImagePlaceholder name={s.key} testid={`segment-placeholder-${s.slug}`} />}
              </div>
              {img && <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent" />}
              <div className="absolute inset-x-0 bottom-0 p-6 text-left">
                <p className={`font-mono text-[10px] uppercase tracking-[0.25em] ${img ? "text-amber-300" : "text-amber-700"}`}>{s.tag}</p>
                <p className={`mt-1 font-serif text-2xl ${img ? "text-white" : "text-stone-900"}`}>{s.key}</p>
                <p className={`mt-1 max-w-xs text-sm ${img ? "text-stone-200" : "text-stone-600"}`}>{s.desc}</p>
                <span className={`mt-3 inline-flex items-center gap-1 text-xs uppercase tracking-widest ${img ? "text-amber-200" : "text-amber-700"}`}>
                  Explore <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                </span>
              </div>
            </button>
          ); })}
        </div>
      </section>

      {/* CATEGORIES */}
      <section data-section="categories" className="mx-auto w-full max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div className="text-center">
          <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-amber-700">Terminals</span>
          <h2 className="mt-2 font-serif text-3xl tracking-tight text-stone-900 sm:text-4xl">Shop by Category</h2>
        </div>
        <FlightPathDivider className="my-6" />
        <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-5">
          {CATEGORIES.map((c, idx) => { const col = findCol(c.name); const img = col.image; return (
            <button
              key={c.name}
              data-testid={`category-tile-${c.name.toLowerCase()}`}
              onClick={() => navigate(`/shop?category=${c.name}`)}
              className={`group relative overflow-hidden rounded-xl border border-[#E7DFD3] ${idx === 0 ? "col-span-2 lg:col-span-1" : ""}`}
            >
              <div className="aspect-[3/4] overflow-hidden bg-[#F3EDE2]">
                {img ? (
                  <img
                    data-testid={`category-img-${c.name.toLowerCase()}`}
                    src={resolveImg(img)}
                    alt={col.image_alt || `${c.name} — PRIYARAA jewellery`}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                ) : <ImagePlaceholder name={c.name} testid={`category-placeholder-${c.name.toLowerCase()}`} />}
              </div>
              {img && <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />}
              <div className="absolute inset-x-0 bottom-0 p-4 text-left">
                <p className={`font-mono text-[9px] uppercase tracking-widest ${img ? "text-amber-300" : "text-amber-700"}`}>{col.tagline || c.tagline}</p>
                <p className={`font-serif text-xl ${img ? "text-white" : "text-stone-900"}`}>{c.name}</p>
              </div>
            </button>
          ); })}
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section data-section="featured" className="bg-[#F3EDE2] py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between">
            <div>
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-amber-700">First Class</span>
              <h2 className="mt-2 font-serif text-3xl tracking-tight text-stone-900 sm:text-4xl">Featured Treasures</h2>
            </div>
            <button
              data-testid="view-all-products"
              onClick={() => navigate("/shop")}
              className="hidden items-center gap-1 text-sm uppercase tracking-widest text-amber-700 hover:text-amber-900 sm:flex"
            >
              View all <ArrowRight size={15} />
            </button>
          </div>
          <div className="mt-8 grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4">
            {featured.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </div>
      </section>

      {/* PROMO BANNERS */}
      <section data-section="banners" className={banners.length ? "mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8 lg:py-14" : ""}>
        {banners.length > 0 && (
          <div className="space-y-6">
            {banners.map((b, i) => (
              <div
                key={i}
                data-testid={`home-banner-${i}`}
                className="group relative overflow-hidden rounded-3xl border border-[#E7DFD3] bg-[#2C221E]"
              >
                {b.image && (
                  <img
                    src={resolveImg(b.image)}
                    alt={b.heading || "Promotion"}
                    className="h-56 w-full object-cover opacity-90 transition-transform duration-700 group-hover:scale-105 sm:h-72 lg:h-80"
                  />
                )}
                <div className={`${b.image ? "absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent" : "bg-[#2C221E]"} flex items-center`}>
                  <div className="p-7 sm:p-10 lg:p-14">
                    {b.eyebrow && (
                      <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-amber-300">{b.eyebrow}</span>
                    )}
                    {b.heading && (
                      <h2 className="mt-2 max-w-xl font-serif text-2xl leading-tight text-[#F4E7C5] sm:text-3xl lg:text-4xl">{b.heading}</h2>
                    )}
                    {b.subtext && (
                      <p className="mt-3 max-w-md text-sm text-stone-200 sm:text-base">{b.subtext}</p>
                    )}
                    {b.cta_label && (
                      <button
                        data-testid={`home-banner-cta-${i}`}
                        onClick={() => (b.cta_link || "").startsWith("http") ? window.open(b.cta_link, "_blank") : navigate(b.cta_link || "/shop")}
                        className="mt-6 inline-flex items-center gap-2 rounded-full gold-gradient px-6 py-3 text-sm font-semibold uppercase tracking-wider text-stone-900 transition-transform hover:scale-105"
                      >
                        {b.cta_label} <ArrowRight size={15} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* PASSPORT STORY */}
      <section data-section="passport" id="story" className="relative overflow-hidden bg-[#2C221E] py-16 text-stone-200 grain lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-amber-500">The Signature Experience</span>
              <h2 className="mt-3 font-serif text-3xl leading-tight text-[#F4E7C5] sm:text-4xl lg:text-5xl">
                The Golden Passport Experience
              </h2>
              <p className="mt-6 max-w-lg font-sans text-base leading-relaxed text-stone-400">
                Every PRIYARAA parcel is sent as a registered first-class flight to your doorstep. Sealed in a
                waterproof matte cream passport cover, complete with an airplane wax seal and a customised
                boarding ticket — unboxing feels like a luxury escape.
              </p>
              <div className="mt-8 grid gap-4 sm:grid-cols-3">
                {[
                  [ShieldCheck, "Skin-Friendly Finish", "Anti-tarnish, nickel-free & long-lasting"],
                  [Plane, "First-Class Delivery", "Insured express air transit worldwide"],
                  [Package, "Passport Box", "Unboxing that feels like a luxury escape"],
                ].map(([Icon, title, sub]) => (
                  <div key={title} className="rounded-xl border border-white/10 bg-white/5 p-4">
                    <Icon size={24} className="text-amber-500" />
                    <p className="mt-3 font-serif text-lg text-[#F4E7C5]">{title}</p>
                    <p className="mt-1 text-xs text-stone-400">{sub}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Passport card visual */}
            <div className="relative mx-auto w-full max-w-sm">
              <div className="rounded-2xl border border-amber-600/40 bg-[#3D302A] p-6 shadow-2xl animate-float">
                <div className="flex items-center justify-between border-b border-amber-600/20 pb-4">
                  <div className="flex items-center gap-2">
                    <AirplaneLogo size={40} />
                    <div>
                      <p className="font-serif text-lg text-[#F4E7C5]">PRIYARAA</p>
                      <p className="font-mono text-[8px] uppercase tracking-widest text-amber-500/70">Passport of Radiance</p>
                    </div>
                  </div>
                  <span className="font-mono text-[9px] text-stone-400">PR-777</span>
                </div>
                <div className="mt-5 grid grid-cols-2 gap-4 font-mono text-[10px] uppercase tracking-wide text-stone-400">
                  <div>
                    <p className="text-amber-500/60">Passenger</p>
                    <p className="text-[#F4E7C5]">Valued Client</p>
                  </div>
                  <div>
                    <p className="text-amber-500/60">Class</p>
                    <p className="text-[#F4E7C5]">Luxury First</p>
                  </div>
                  <div>
                    <p className="text-amber-500/60">Origin</p>
                    <p className="text-[#F4E7C5]">Our Atelier</p>
                  </div>
                  <div>
                    <p className="text-amber-500/60">Destination</p>
                    <p className="text-[#F4E7C5]">Your Doorstep</p>
                  </div>
                </div>
                <div className="mt-6 flex items-center justify-between">
                  <div className="barcode h-8 w-32" />
                  <span className="font-mono text-[9px] text-amber-500/70">SEAT 1A</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OFFERS - BOARDING PASSES */}
      <section data-section="offers" id="offers" className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div className="text-center">
          <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-amber-700">Privileges</span>
          <h2 className="mt-2 font-serif text-3xl tracking-tight text-stone-900 sm:text-4xl">
            Boarding Pass Offers
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-sm text-stone-500">
            Unlock instant upgrades and flight discounts on your jewellery order.
          </p>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {offers.map((o) => (
            <div
              key={o.id}
              data-testid={`offer-card-${o.id}`}
              className="ticket-notch overflow-hidden rounded-2xl border-2 border-dashed border-amber-600/40 bg-white shadow-sm"
            >
              <div className="flex items-center justify-between gold-gradient px-5 py-3">
                <div className="flex items-center gap-2">
                  <Plane size={16} className="text-stone-900" />
                  <span className="font-mono text-[10px] uppercase tracking-widest text-stone-900">
                    {o.flight_class}
                  </span>
                </div>
                <span className="font-mono text-[10px] text-stone-900">GATE {o.gate}</span>
              </div>
              <div className="p-5">
                <p className="font-serif text-2xl text-stone-900">{o.discount}</p>
                <p className="mt-1 font-serif text-lg text-amber-800">{o.title}</p>
                <p className="mt-2 text-sm text-stone-500">{o.description}</p>
                <div className="mt-4 flex items-center justify-between border-t border-dashed border-[#E7DFD3] pt-4">
                  <div>
                    <p className="font-mono text-[9px] uppercase tracking-widest text-stone-400">Promo Code</p>
                    <p className="font-mono text-sm font-semibold text-stone-900">{o.promo_code}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-[9px] uppercase tracking-widest text-stone-400">Valid Until</p>
                    <p className="font-mono text-xs text-stone-700">{o.expiry || "—"}</p>
                  </div>
                </div>
                <div className="barcode mt-4 h-7 w-full opacity-80" />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ABOUT */}
      <section data-section="about" id="about" className="bg-[#F3EDE2] py-16 lg:py-24">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <div className="relative overflow-hidden rounded-2xl">
            <img
              src="/products/about.jpg"
              alt="PRIYARAA jewellery collection"
              className="h-[420px] w-full object-cover"
            />
          </div>
          <div>
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-amber-700">Our Story</span>
            <h2 className="mt-2 font-serif text-3xl leading-tight text-stone-900 sm:text-4xl">
              Jewellery that travels the world in first class
            </h2>
            <p className="mt-6 font-sans leading-relaxed text-stone-600">
              PRIYARAA was born from a love of travel and timeless style. Each piece is designed in-house with an
              anti-tarnish, skin-friendly finish — then packaged inside our signature golden passport box, stamped
              with our airplane emblem. Wherever you are in the world, we deliver radiance to your doorstep.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-6">
              {[
                ["Anti-Tarnish", "Long-Lasting Shine"],
                ["Skin-Safe", "Nickel-Free Finish"],
                ["Worldwide", "Insured Shipping"],
              ].map(([n, l]) => (
                <div key={l}>
                  <p className="font-serif text-3xl gold-text">{n}</p>
                  <p className="mt-1 text-xs uppercase tracking-widest text-stone-500">{l}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section data-section="testimonials" className={testimonials.length ? "bg-[#F3EDE2] py-16 lg:py-24" : ""}>
        {testimonials.length > 0 && (
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-amber-700">First-Class Reviews</span>
              <h2 className="mt-2 font-serif text-3xl tracking-tight text-stone-900 sm:text-4xl">Loved by Travellers</h2>
            </div>
            <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {testimonials.slice(0, 6).map((t) => (
                <div key={t.id} data-testid={`testimonial-${t.id}`} className="relative rounded-2xl border border-[#E7DFD3] bg-white p-6 shadow-sm">
                  <Quote size={26} className="text-amber-300" />
                  <div className="mt-2 flex gap-0.5">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} size={14} className={i < (t.rating || 5) ? "fill-amber-400 text-amber-400" : "text-stone-300"} />
                    ))}
                  </div>
                  <p className="mt-3 font-sans text-sm leading-relaxed text-stone-700">“{t.quote}”</p>
                  <div className="mt-5 flex items-center gap-3 border-t border-[#EFE8DC] pt-4">
                    <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded-full border border-[#E7DFD3] bg-[#F3EDE2]">
                      {t.image
                        ? <img src={resolveImg(t.image)} alt={t.name} className="h-full w-full object-cover" />
                        : <div className="flex h-full w-full items-center justify-center font-serif text-sm text-amber-700">{(t.name || "?").charAt(0)}</div>}
                    </div>
                    <div>
                      <p className="font-serif text-base text-stone-900">{t.name}</p>
                      {t.location && <p className="text-xs text-stone-500">{t.location}</p>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* INSTAGRAM */}
      <section data-section="instagram" className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
        <div className="text-center">
          <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-amber-700">#PriyaraaJourney</span>
          <h2 className="mt-2 font-serif text-3xl tracking-tight text-stone-900 sm:text-4xl">Follow the Journey</h2>
          <a
            data-testid="instagram-follow-link"
            href={settings?.instagram_url || "#"}
            target="_blank"
            rel="noreferrer"
            className="mt-3 inline-flex items-center gap-2 text-sm uppercase tracking-widest text-amber-700 hover:text-amber-900"
          >
            <Instagram size={16} /> @priyaraa
          </a>
        </div>
        <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            "/products/ox-earrings.jpg",
            "/products/at-necklace.jpg",
            "/products/ox-bracelet.jpg",
            "/products/at-ring.jpg",
          ].map((src, i) => (
            <a
              key={i}
              href={settings?.instagram_url || "#"}
              target="_blank"
              rel="noreferrer"
              className="group relative aspect-square overflow-hidden rounded-lg"
            >
              <img src={src} alt="Instagram post" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
              <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 transition-opacity group-hover:opacity-100">
                <Instagram size={24} className="text-white" />
              </div>
            </a>
          ))}
        </div>
      </section>
    </div>
  );
}
